import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG, getAllowedGroupClassificationKeys, getBrowseDaysBeforeToday, getBrowseDaysAfterToday, getPresentmentPublicationKeys, isOrderCreationAllowed } from './config';
import type {
  AdFormatInfo,
  ClassificationInfo,
  ClassificationTree,
  ClassificationAdFormatAssociation,
  ClassificationCustomFieldAssociation,
  ClassificationPackageAssociation,
  PackageInfo,
  PackageFeatureAssociation,
  FeatureInfo,
  PublicationInfo,
  CustomFieldInfo,
  ServiceInfo,
  PriceOrderRequest,
  PlaceOrderRequest,
  PlaceOrderResult,
  GetOrderRunDatesResponse,
  GetScheduledAdsRequest,
  GetScheduledAdsResponse,
  GetScheduledRetailClassDisplayAdsResponse,
  BrowseAdsRequest,
  BrowseAdsResponse,
  BrowseCountsResponse,
  ClassificationAdsRequest,
  ClassificationAdsResponse,
  PaymentConfig,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  PaidCheckoutPreflightRequest,
  PaidCheckoutPreflightResponse,
  PaidCheckoutRequest,
  PaidCheckoutResponse,
} from '@/types/classifiedOrder';

/**
 * API error with additional context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Prevent order-entry calls when the effective tenant/site policy hides order creation.
 * This is intentionally a client-side presentation guard, not an API authorization mechanism.
 */
function requireOrderCreation(): void {
  if (!isOrderCreationAllowed()) {
    throw new ApiError('Order creation is not available for this site.', 403);
  }
}

/**
 * Converts PascalCase keys to camelCase
 */
function toCamelCase(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      (result as Record<string, unknown>)[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
      return result;
    }, {} as Record<string, unknown>);
  }
  return obj;
}

/**
 * Creates and configures the axios instance for API calls
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    // Note: baseURL is set dynamically in the request interceptor
    // because the config may not be loaded when this client is created
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // Transform response data from PascalCase to camelCase
    transformResponse: [
      // First, parse JSON (default axios behavior)
      (data) => {
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      },
      // Then convert keys to camelCase
      (data) => toCamelCase(data),
    ],
  });

  // Request interceptor to dynamically set baseURL and API key
  // This is needed because config may load after the client is created
  client.interceptors.request.use(
    (config) => {
      // Dynamically set baseURL from current config
      config.baseURL = API_CONFIG.baseUrl;
      
      // Dynamically set API key header
      config.headers['X-Api-Key'] = API_CONFIG.apiKey;
      
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL || ''}${config.url}`);
      return config;
    },
    (error) => {
      console.error('[API Request Error]', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => {
      console.log(`[API Response] ${response.status} ${response.config.url}`);
      return response;
    },
    (error: AxiosError) => {
      const message = extractErrorMessage(error);
      console.error('[API Error]', {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message,
      });
      throw new ApiError(message, error.response?.status, error);
    }
  );

  return client;
}

/**
 * Extracts a user-friendly error message from an axios error
 */
function extractErrorMessage(error: AxiosError): string {
  if (error.response) {
    // Server responded with an error
    const data = error.response.data as { message?: string; Message?: string } | string;
    if (typeof data === 'string') {
      return data;
    }
    return data?.message || data?.Message || `Server error: ${error.response.status}`;
  } else if (error.request) {
    // Request was made but no response received
    return 'Unable to connect to the server. Please check your network connection.';
  } else {
    // Error in setting up the request
    return error.message || 'An unexpected error occurred';
  }
}

// Create the API client instance
const apiClient = createApiClient();

// ============================================================================
// Classified Order Entry API Service
// ============================================================================

export const classifiedOrderApi = {
  // --------------------------------------------------------------------------
  // GET Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get all classifications
   */
  async getClassifications(): Promise<ClassificationInfo[]> {
    const response = await apiClient.get<ClassificationInfo[]>(
      API_CONFIG.endpoints.classifications
    );
    return response.data;
  },

  /**
   * Get classification tree hierarchy
   */
  async getClassificationsTree(): Promise<ClassificationTree> {
    const response = await apiClient.get<ClassificationTree>(
      API_CONFIG.endpoints.classificationsTree
    );
    return response.data;
  },

  /**
   * Get packages, optionally filtered by classification
   * NOTE: Backend route is packages/{leafClassificationKey?} - optional path parameter
   */
  async getPackages(leafClassificationKey?: number): Promise<PackageInfo[]> {
    requireOrderCreation();
    // Backend expects: /coe/packages or /coe/packages/{leafClassificationKey} (path parameter)
    const url = leafClassificationKey
      ? `${API_CONFIG.endpoints.packages}/${leafClassificationKey}`
      : API_CONFIG.endpoints.packages;
    const response = await apiClient.get<PackageInfo[]>(url);
    return response.data;
  },

  /**
   * Get ad formats for a classification
   * NOTE: Backend route is adformats/{leafClassificationKey} - requires path parameter
   */
  async getAdFormats(leafClassificationKey?: number): Promise<AdFormatInfo[]> {
    requireOrderCreation();
    // If no classification key provided, return empty array
    // (ad formats are typically classification-specific, and backend requires the key in path)
    if (!leafClassificationKey) {
      return [];
    }
    // Backend expects: /coe/adformats/{leafClassificationKey} (path parameter, not query)
    const url = `${API_CONFIG.endpoints.adFormats}/${leafClassificationKey}`;
    const response = await apiClient.get<AdFormatInfo[]>(url);
    return response.data;
  },

  /**
   * Get features, optionally filtered by package
   * NOTE: Backend route is features/{packageKey?} - optional path parameter
   */
  async getFeatures(packageKey?: number): Promise<FeatureInfo[]> {
    requireOrderCreation();
    // Backend expects: /coe/features or /coe/features/{packageKey} (path parameter)
    const url = packageKey
      ? `${API_CONFIG.endpoints.features}/${packageKey}`
      : API_CONFIG.endpoints.features;
    const response = await apiClient.get<FeatureInfo[]>(url);
    return response.data;
  },

  /**
   * Get publications list
   */
  async getPublications(): Promise<PublicationInfo[]> {
    requireOrderCreation();
    const response = await apiClient.get<PublicationInfo[]>(
      API_CONFIG.endpoints.publications
    );
    return response.data;
  },

  /**
   * Get custom fields, optionally filtered by classification
   * This endpoint is shared by browse presentment and order entry, so it remains available
   * when customer order creation is hidden.
   */
  async getCustomFields(leafClassificationKey?: number): Promise<CustomFieldInfo[]> {
    const url = leafClassificationKey
      ? `${API_CONFIG.endpoints.customFields}/${leafClassificationKey}`
      : API_CONFIG.endpoints.customFields;
    const response = await apiClient.get<CustomFieldInfo[]>(url);
    return response.data;
  },

  /**
   * Get service info (good test of API connectivity)
   */
  async getServiceInfo(): Promise<ServiceInfo> {
    const response = await apiClient.get<ServiceInfo>(
      API_CONFIG.endpoints.serviceInfo
    );
    return response.data;
  },

  /**
   * Get order run dates
   */
  async getOrderRunDates(orderNumber: string): Promise<GetOrderRunDatesResponse> {
    requireOrderCreation();
    const response = await apiClient.get<GetOrderRunDatesResponse>(
      `${API_CONFIG.endpoints.orderRunDates}/${orderNumber}`
    );
    return response.data;
  },

  // --------------------------------------------------------------------------
  // Association Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get classification-ad format associations
   */
  async getClassificationAdFormatAssociations(): Promise<ClassificationAdFormatAssociation[]> {
    requireOrderCreation();
    const response = await apiClient.get<ClassificationAdFormatAssociation[]>(
      API_CONFIG.endpoints.classificationAdFormatAssociations
    );
    return response.data;
  },

  /**
   * Get classification-custom field associations
   */
  async getClassificationCustomFieldAssociations(): Promise<ClassificationCustomFieldAssociation[]> {
    requireOrderCreation();
    const response = await apiClient.get<ClassificationCustomFieldAssociation[]>(
      API_CONFIG.endpoints.classificationCustomFieldAssociations
    );
    return response.data;
  },

  /**
   * Get classification-package associations
   */
  async getClassificationPackageAssociations(): Promise<ClassificationPackageAssociation[]> {
    requireOrderCreation();
    const response = await apiClient.get<ClassificationPackageAssociation[]>(
      API_CONFIG.endpoints.classificationPackageAssociations
    );
    return response.data;
  },

  /**
   * Get package-feature associations
   */
  async getPackageFeatureAssociations(): Promise<PackageFeatureAssociation[]> {
    requireOrderCreation();
    const response = await apiClient.get<PackageFeatureAssociation[]>(
      API_CONFIG.endpoints.packageFeatureAssociations
    );
    return response.data;
  },

  // --------------------------------------------------------------------------
  // POST Endpoints
  // --------------------------------------------------------------------------

  /**
   * Calculate price for an order
   */
  async priceOrder(request: PriceOrderRequest): Promise<number> {
    requireOrderCreation();
    const response = await apiClient.post<number>(
      API_CONFIG.endpoints.priceOrder,
      request
    );
    return response.data;
  },

  /**
   * Place an order
   * Returns the order number as a string
   */
  async placeOrder(request: PlaceOrderRequest): Promise<string> {
    requireOrderCreation();
    const response = await apiClient.post<string | { orderNumber: string }>(
      API_CONFIG.endpoints.placeOrder,
      request
    );
    // Handle both plain string response and object with orderNumber property
    const data = response.data;
    if (typeof data === 'string') {
      return data;
    } else if (data && typeof data === 'object' && 'orderNumber' in data) {
      return data.orderNumber;
    }
    // Fallback: try to convert to string
    return String(data);
  },

  /**
   * Place an order and return the review decision made during persistence.
   */
  async placeOrderWithResult(request: PlaceOrderRequest): Promise<PlaceOrderResult> {
    requireOrderCreation();
    const response = await apiClient.post<PlaceOrderResult>(
      API_CONFIG.endpoints.placeOrderWithResult,
      request
    );
    return response.data;
  },

  /**
   * Validate an order before placing
   */
  async validateOrder(request: PlaceOrderRequest): Promise<boolean> {
    requireOrderCreation();
    const response = await apiClient.post<boolean>(
      API_CONFIG.endpoints.validateOrder,
      request
    );
    return response.data;
  },

  /**
   * Get scheduled ads for a publication date
   */
  async getScheduledAds(request: GetScheduledAdsRequest): Promise<GetScheduledAdsResponse> {
    const response = await apiClient.post<GetScheduledAdsResponse>(
      API_CONFIG.endpoints.scheduledAds,
      request
    );
    return response.data;
  },

  /**
   * Get scheduled retail classified display ads for a publication date
   * These are business image ads that should be displayed prominently
   */
  async getScheduledRetailClassDisplayAds(request: GetScheduledAdsRequest): Promise<GetScheduledRetailClassDisplayAdsResponse> {
    const response = await apiClient.post<GetScheduledRetailClassDisplayAdsResponse>(
      API_CONFIG.endpoints.scheduledRetailClassDisplayAds,
      request
    );
    return response.data;
  },

  /**
   * Get ads for browsing/presentment within a date range around today.
   * Returns both regular classified ads and retail display ads, deduplicated by order number.
   * Automatically includes effective site presentment filters if configured.
   * An explicit publications value remains authoritative over site configuration.
   * @param request Optional request with daysBeforeToday (default 6), daysAfterToday (default 7), and publications filter.
   */
  async getBrowseAds(request?: BrowseAdsRequest): Promise<BrowseAdsResponse> {
    // Use the effective site presentment filter only when the caller did not provide one.
    const publications = request?.publications === undefined
      ? getPresentmentPublicationKeys()
      : request.publications;
    const allowedGroups = request?.allowedGroupClassificationKeys ?? getAllowedGroupClassificationKeys();
    
    // Convert to PascalCase for C# backend
    const pascalCaseRequest = {
      DaysBeforeToday: request?.daysBeforeToday,
      DaysAfterToday: request?.daysAfterToday,
      Publications: publications,
      AllowedGroupClassificationKeys: allowedGroups,
    };
    
    // This endpoint can take longer as it fetches ads for multiple dates
    // Pass pdfImageUrls=true to get cached Azure URLs instead of Base64 for PDF Builder ads
    const response = await apiClient.post<BrowseAdsResponse>(
      `${API_CONFIG.endpoints.browseAds}?pdfImageUrls=true`,
      pascalCaseRequest,
      { timeout: 120000 } // 2 minute timeout for this endpoint
    );
    return response.data;
  },

  /**
   * Get ad counts per classification for lazy loading.
   * This is a lightweight endpoint for fast initial page load - returns only counts, not full ad data.
   * Automatically includes effective site presentment filters if configured.
   * @param daysBeforeToday Number of days before today (default 6).
   * @param daysAfterToday Number of days after today (default 7).
   */
  async getBrowseCounts(
    daysBeforeToday?: number,
    daysAfterToday?: number,
    overrideAllowedGroups?: number[] | null
  ): Promise<BrowseCountsResponse> {
    // Get the effective site presentment filter from config.
    const allowedPublications = getPresentmentPublicationKeys();
    // Use override groups if explicitly passed, otherwise use config default
    const allowedGroups = overrideAllowedGroups !== undefined
      ? overrideAllowedGroups
      : getAllowedGroupClassificationKeys();

    // Build request with PascalCase for C# backend
    const pascalCaseRequest = {
      DaysBeforeToday: daysBeforeToday ?? getBrowseDaysBeforeToday(),
      DaysAfterToday: daysAfterToday ?? getBrowseDaysAfterToday(),
      Publications: allowedPublications,
      AllowedGroupClassificationKeys: allowedGroups,
    };
    
    const response = await apiClient.post<BrowseCountsResponse>(
      API_CONFIG.endpoints.browseCounts,
      pascalCaseRequest,
      { timeout: 120000 }  // 2 minute timeout - endpoint may be slow
    );
    return response.data;
  },

  /**
   * Get ads for a single classification.
   * Used for lazy loading when a classification is selected.
   * Automatically includes the effective site presentment filter when publications are not specified.
   * An explicit publications value remains authoritative over site configuration.
   * @param request Request containing classificationKey and optional date range.
   */
  async getClassificationAds(request: ClassificationAdsRequest): Promise<ClassificationAdsResponse> {
    // Use the effective site presentment filter only when the caller did not provide one.
    const publications = request.publications !== undefined
      ? request.publications
      : getPresentmentPublicationKeys();
    
    // Convert to PascalCase for C# backend
    const pascalCaseRequest: Record<string, unknown> = {
      ClassificationKey: request.classificationKey,
      DaysBeforeToday: request.daysBeforeToday,
      DaysAfterToday: request.daysAfterToday,
      Publications: publications,
    };

    // Add pagination/search params if provided
    if (request.skip != null) pascalCaseRequest.Skip = request.skip;
    if (request.take != null) pascalCaseRequest.Take = request.take;
    if (request.searchText) pascalCaseRequest.SearchText = request.searchText;

    // Pass pdfImageUrls=true to get cached Azure URLs instead of Base64 for PDF Builder ads
    const response = await apiClient.post<ClassificationAdsResponse>(
      `${API_CONFIG.endpoints.classificationAds}?pdfImageUrls=true`,
      pascalCaseRequest,
      { timeout: 120000 } // 2 minute timeout - loading many ads can take time
    );
    return response.data;
  },

  /**
   * Cancel upcoming runs for an order
   */
  async cancelUpcomingRuns(orderNumber: string): Promise<number> {
    const response = await apiClient.post<number>(
      API_CONFIG.endpoints.cancelUpcomingRuns,
      JSON.stringify(orderNumber),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  },

  // --------------------------------------------------------------------------
  // Payment Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get payment configuration for the Celero tokenizer
   * Returns the tokenizer URL and public API key needed for client-side card entry
   */
  async getPaymentConfig(): Promise<PaymentConfig> {
    requireOrderCreation();
    const response = await apiClient.get<PaymentConfig>(
      API_CONFIG.endpoints.paymentConfig
    );
    return response.data;
  },

  /**
   * Process a payment using a token from the Celero tokenizer
   * @param request The payment request containing token, amount, and billing info
   * @returns The payment response indicating success or failure with actual amounts charged
   */
  async processPayment(request: ProcessPaymentRequest): Promise<ProcessPaymentResponse> {
    requireOrderCreation();
    const response = await apiClient.post<ProcessPaymentResponse>(
      API_CONFIG.endpoints.processPayment,
      request
    );
    return response.data;
  },

  /** Validate a complete paid order and its AccountScout configuration before card entry. */
  async preflightPaidCheckout(
    request: PaidCheckoutPreflightRequest
  ): Promise<PaidCheckoutPreflightResponse> {
    requireOrderCreation();
    const response = await apiClient.post<PaidCheckoutPreflightResponse>(
      API_CONFIG.endpoints.paidCheckoutPreflight,
      request
    );
    return response.data;
  },

  /** Charge and persist a paid order using the server-controlled checkout workflow. */
  async paidCheckout(request: PaidCheckoutRequest): Promise<PaidCheckoutResponse> {
    requireOrderCreation();
    const response = await apiClient.post<PaidCheckoutResponse>(
      API_CONFIG.endpoints.paidCheckout,
      request,
      { timeout: 480000 }
    );
    return response.data;
  },
};

export default classifiedOrderApi;
